import * as d3 from 'd3';
import { isNil } from 'lodash';

import { ProcessingSource } from '@/api/historicalData';
import { DOT_RADIUS, Line } from '@/charts/lineChart';
import { AlertLine, ColorSet, D3ElementSelection, MarginConfig } from '@/charts/types';
import { isFiniteNumber } from '@/utils';

export const getXValue = (d: object, xKey: PropertyKey): string => {
    if (!d) {
        // eslint-disable-next-line no-console
        console.log('%c getXValue, No datum provided', 'background-color: #413d27; color: #f6eda6', d);
        return '';
    }

    if (!Object.hasOwn(d, xKey)) {
        // eslint-disable-next-line no-console
        console.log(
            `%c The xKey ${String(xKey)} does not exist in the dataset`,
            'background-color: #413d27; color: #f6eda6'
        );
        return '';
    }
    const xValue: unknown = Reflect.get(d, xKey);
    if (typeof xValue !== 'string') {
        // eslint-disable-next-line no-console
        console.log('%c The xValue is not a string', 'background-color: #413d27; color: #f6eda6', xValue);
        return '';
    }
    return xValue;
};

export const getYValue = (d: object, yKey: PropertyKey): number => {
    if (!d) {
        // eslint-disable-next-line no-console
        console.log('%c getYValue, No datum provided', 'background-color: #413d27; color: #f6eda6', d);
        return 0;
    }
    if (!Object.hasOwn(d, yKey)) {
        // eslint-disable-next-line no-console
        console.log(
            `%c The yKey ${String(yKey)} does not exist in the datum`,
            'background-color: #413d27; color: #f6eda6'
        );
        return 0;
    }
    const yValue: unknown = Reflect.get(d, yKey);
    if (typeof yValue !== 'number') {
        // eslint-disable-next-line no-console
        console.log('%c The yValue is not a number', 'background-color: #413d27; color: #f6eda6', yValue);
        return 0;
    }
    return yValue;
};

export const boundsExtent = (dataBounds: { max: number; min: number }[]): { max: number; min: number } | null => {
    if (!dataBounds.length) return null;

    return dataBounds.reduce(({ min: accMin, max: accMax }, { min, max }) => ({
        min: Math.min(accMin, min),
        max: Math.max(accMax, max),
    }));
};

export const getYRange = <Datum extends GenericObject>(
    dataset: Datum[],
    yKeyName: PropertyKey,
    y2KeyName?: PropertyKey,
    padding: number = 0.1
): [number, number] => {
    const yAxisMin = (d3.min(dataset, (datum) => getYValue(datum, yKeyName)) ?? 0) - padding;
    const yAxisMax = (d3.max(dataset, (datum) => getYValue(datum, yKeyName)) ?? 0) + padding;
    if (!y2KeyName) {
        return [yAxisMin, yAxisMax];
    }
    const y2AxisMin = (d3.min(dataset, (datum) => getYValue(datum, y2KeyName)) ?? 0) - padding;
    const y2AxisMax = (d3.max(dataset, (datum) => getYValue(datum, y2KeyName)) ?? 0) + padding;
    return [Math.min(yAxisMin, y2AxisMin), Math.max(yAxisMax, y2AxisMax)];
};

type GetYRangeForMultipleLinesProps<D, K extends keyof D> = {
    dataset: D[];
    lines: { valueKey: keyof D; [x: string]: keyof D }[];
    dataYRange?: { yMin: number; yMax: number };
    minYRange?: { yMin?: number; yMax?: number };
    alertLines?: AlertLine[];
    options?: { rangePaddingPercent?: number; scaleChartToFitAllAlerts?: boolean; lineValueKey?: K };
};

export const getYRangeForMultipleLines = <Datum extends GenericObject, K extends keyof Datum = 'valueKey'>({
    dataset,
    lines,
    dataYRange,
    minYRange,
    alertLines = [],
    options = {},
}: GetYRangeForMultipleLinesProps<Datum, K>): [number, number] => {
    const { rangePaddingPercent = 6, scaleChartToFitAllAlerts = false, lineValueKey = 'valueKey' } = options;
    let yAxisMin = Infinity;
    let yAxisMax = -Infinity;

    if (dataYRange) {
        yAxisMin = dataYRange.yMin;
        yAxisMax = dataYRange.yMax;
    } else {
        for (const line of lines) {
            /*
            not every datapoint in the dataset will have a value for every line
            so we consider the min and max across datapoints that have values only
            we can't default to 0 in the absence of a value because 0 might be a valid value for some charts
         */
            const withLineDataOnly = dataset.filter((datum) => Object.hasOwn(datum, line[lineValueKey]));
            const [min, max] = d3.extent(withLineDataOnly, (datum) => getYValue(datum, line[lineValueKey]));

            yAxisMin = isNil(min) ? yAxisMin : Math.min(yAxisMin, min);
            yAxisMax = isNil(max) ? yAxisMax : Math.max(yAxisMax, max);
        }
    }

    if (minYRange) {
        yAxisMin = Math.min(yAxisMin, minYRange.yMin ?? Infinity);
        yAxisMax = Math.max(yAxisMax, minYRange.yMax ?? -Infinity);
    }

    if (scaleChartToFitAllAlerts) {
        for (const { value } of alertLines) {
            if (isFiniteNumber(value)) {
                yAxisMin = Math.min(yAxisMin, value);
                yAxisMax = Math.max(yAxisMax, value);
            }
        }
    }
    const rangePadding = (yAxisMax - yAxisMin) * (rangePaddingPercent / 100);
    yAxisMin -= rangePadding;
    yAxisMax += rangePadding;
    return [yAxisMin, yAxisMax];
};

export type GenericObject = Record<string, string | number | boolean>;

export const findRelativeAncestor = (element: Element) => {
    let ancestor = element.parentElement;
    while (ancestor && window.getComputedStyle(ancestor).position !== 'static') {
        ancestor = ancestor.parentElement;
    }
    return ancestor ?? document.getElementsByName('body')[0];
};

export const positionCursorTooltip = ({
    tooltip,
    cursorX,
    chartBounds,
    relativeAncestor,
    tipXOffset = -8,
    tipYOffset = 0,
}: {
    tooltip: D3ElementSelection;
    cursorX: number;
    chartBounds: DOMRect;
    relativeAncestor: Element;
    margin: MarginConfig;
    tipXOffset?: number;
    tipYOffset?: number;
}) => {
    const relativeAncestorBounds = relativeAncestor.getBoundingClientRect();
    const tooltipBounds = tooltip.node()?.getBoundingClientRect();
    tooltip
        .style('left', `${chartBounds.left - relativeAncestorBounds.left + cursorX + tipXOffset}px`)
        .style('top', `${chartBounds.top - relativeAncestorBounds.top - (tooltipBounds?.height ?? 0) + tipYOffset}px`);
};

export const makeColor = (color: string, opacity: number = 1): string =>
    d3.color(color)?.copy({ opacity }).formatRgb() || 'black';

export const makeColorSet = (primaryColor: string, secondaryColor: string): ColorSet => ({
    circle: makeColor(primaryColor),
    line: makeColor(primaryColor),
    area: makeColor(primaryColor, 0.3),
    secondary: {
        circle: makeColor(secondaryColor),
        line: makeColor(secondaryColor),
        area: makeColor(secondaryColor, 0.3),
    },
});

export const forEachSensor = (
    datasetWithMultipleSensors: object,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (sensorPosition: string, dataPerSensor: any, index: number) => void
) => {
    let index = 0;
    for (const [sensorPosition, dataPerSensor] of Object.entries(datasetWithMultipleSensors)) {
        callback(sensorPosition, dataPerSensor, index);
        index++;
    }
};

/**
 * Open dot for embedded, regular dot for HDR
 */
export const addRenderersByProcessingSource = <D extends { processing_source: ProcessingSource }, L extends Line<D>>(
    lines: L[],
    // We used to only degrade embedded dots if data had any hdr. Leaving this plumbed for now in case
    // we decide we do want data-dependent rendering, but if this is still unused a few months into V2,
    // feel free to purge (and maybe inline the dotRenderer into callers' line configs and get rid of this
    // method entirely).
    _dataset: D[] | undefined
) => {
    return lines.map((l) => ({
        ...l,
        dotRenderer: degradedDotRenderer<D>((d) => d.processing_source === 'embedded'),
    }));
};

export const degradedDotRenderer =
    <D>(isDegraded: (datum: D) => boolean): Line<D>['dotRenderer'] =>
    ({ target, x, y, line: { color } }) => {
        target
            .append('circle')
            .attr('cx', x)
            .attr('cy', y)
            .attr('r', (datum) => DOT_RADIUS - (isDegraded(datum) ? 0.5 : 0))
            .attr('fill', (datum) => (isDegraded(datum) ? 'white' : color.line))
            .attr('stroke', (datum) => (isDegraded(datum) ? (color as ColorSet).line : 'none'));
    };
